import { Link } from 'react-router-dom';
import { ARTICLES } from '@/mocks/articles';

type Article = (typeof ARTICLES)[number];

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'wide';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/archive/${article.slug}`}
        className="group flex gap-4 p-3 rounded-lg border border-background-200 bg-background-100/60 hover:border-primary-500/60 transition-colors cursor-pointer"
      >
        <div className="w-20 h-20 md:w-24 md:h-24 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 黑料百科考古档案`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="min-w-0 flex flex-col justify-center">
          <span className="text-[11px] tracking-widest text-primary-500">
            {article.id} · {article.categoryName}
          </span>
          <h3 className="mt-1.5 text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
            {article.title}
          </h3>
          <span className="mt-2 text-xs text-foreground-600">
            {article.date} · 阅读 {article.readTime} 分钟
          </span>
        </div>
      </Link>
    );
  }

  if (variant === 'wide') {
    return (
      <Link
        to={`/archive/${article.slug}`}
        className="group grid grid-cols-1 md:grid-cols-2 rounded-lg overflow-hidden border border-background-200 bg-background-100/60 hover:border-primary-500/60 transition-colors cursor-pointer"
      >
        <div className="w-full h-56 md:h-full md:min-h-[300px] overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 黑料百科考古档案`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
        </div>
        <div className="p-6 md:p-8 flex flex-col">
          <div className="flex items-center gap-3 text-[11px] tracking-widest">
            <span className="px-2.5 py-1 rounded-full bg-primary-500/10 text-primary-500">
              {article.categoryName}
            </span>
            <span className="text-foreground-600">{article.id}</span>
          </div>
          <h3 className="mt-4 font-heading text-xl md:text-2xl text-foreground-950 leading-snug group-hover:text-primary-500 transition-colors">
            {article.title}
          </h3>
          <p className="mt-3 text-sm text-foreground-600 leading-relaxed line-clamp-3">
            {article.excerpt}
          </p>
          <div className="mt-auto pt-6 flex items-center gap-4 text-xs text-foreground-600">
            <span>档案员 {article.author}</span>
            <span className="w-1 h-1 rounded-full bg-background-400"></span>
            <span>{article.date}</span>
            <span className="w-1 h-1 rounded-full bg-background-400"></span>
            <span>{article.readTime} 分钟</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/archive/${article.slug}`}
      className="group flex flex-col h-full rounded-lg overflow-hidden border border-background-200 bg-background-100/60 hover:border-primary-500/60 transition-colors cursor-pointer"
    >
      <div className="w-full h-48 overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 黑料百科考古档案`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between text-[11px] tracking-widest">
          <span className="text-primary-500">{article.categoryName}</span>
          <span className="text-foreground-600">{article.date}</span>
        </div>
        <h3 className="mt-3 font-heading text-lg text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed line-clamp-3">
          {article.excerpt}
        </p>
        <div className="mt-auto pt-5 flex items-center justify-between text-xs text-foreground-600">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line text-primary-500"></i>
            {article.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line"></i>
            {article.views}
          </span>
        </div>
      </div>
    </Link>
  );
}